/**
 * Boolean Write Button Component
 * Configurable button for writing boolean values to SCADA tags
 */

import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Power as PowerIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Warning as WarningIcon,
  FlashOn as FlashIcon,
} from '@mui/icons-material';

interface BooleanWriteButtonConfig {
  id: number;
  name: string;
  description: string;
  target_tag_name: string;
  target_tag_current_value: boolean;
  write_mode: 'fixed' | 'toggle' | 'pulse';
  write_value: boolean;
  button_heading: string;
  button_text: string;
  text_when_false: string;
  text_when_true: string;
  pulse_duration_seconds: number;
  pulse_text: string;
  button_size: 'small' | 'medium' | 'large';
  active_color: string;
  inactive_color: string;
  disabled_color: string;
  requires_confirmation: boolean;
  confirmation_message: string;
  position_x: number;
  position_y: number;
  z_index: number;
  is_active: boolean;
  can_write: boolean;
}

interface BooleanWriteButtonProps {
  config: BooleanWriteButtonConfig;
  writeEnabled: boolean;
  hasWritePermission: boolean;
  onWrite: (buttonId: number, writeReason?: string) => Promise<{ success: boolean; error?: string }>;
  onRefresh?: () => void;
  onGetUpdatedValue?: () => Promise<boolean>; // New callback to get updated tag value after write
}

export const BooleanWriteButton: React.FC<BooleanWriteButtonProps> = ({
  config,
  writeEnabled,
  hasWritePermission,
  onWrite,
  onRefresh,
  onGetUpdatedValue,
}) => {
  // Handle missing config gracefully
  if (!config) {
    return (
      <Box
        sx={{
          padding: '8px 16px',
          backgroundColor: '#ffebee',
          border: '1px solid #f44336',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#d32f2f',
          textAlign: 'center',
          minWidth: '120px'
        }}
      >
        ⚠️ Button Config Missing
      </Box>
    );
  }

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isWriting, setIsWriting] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [pulseProgress, setPulseProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [writeReason, setWriteReason] = useState('');

  // Determine button state and styling
  const getButtonState = () => {
    // Always show button, just disable functionality if needed
    if (!config.is_active) {
      return 'disabled';
    }
    
    // Visual state based on current tag value
    return config.target_tag_current_value ? 'active' : 'inactive';
  };
  
  const isButtonDisabled = () => {
    return !config.can_write || !writeEnabled || !hasWritePermission || isWriting || isPulsing;
  };

  const getDisplayText = () => {
    if (isPulsing && config.write_mode === 'pulse' && config.pulse_text) {
      return config.pulse_text;
    } else if (config.write_mode === 'toggle' && config.text_when_false && config.text_when_true) {
      return config.target_tag_current_value ? config.text_when_true : config.text_when_false;
    }
    return config.button_text;
  };

  const getWriteValue = () => {
    if (config.write_mode === 'toggle') {
      // Toggle: write opposite of current value
      return !config.target_tag_current_value;
    } else {
      // Fixed: write the configured value
      return config.write_value;
    }
  };

  const getButtonColor = () => {
    const state = getButtonState();
    switch (state) {
      case 'active':
        return config.active_color;
      case 'inactive':
        return config.inactive_color;
      case 'disabled':
      default:
        return config.disabled_color;
    }
  };

  const getButtonIcon = () => {
    if (isButtonDisabled() && !isPulsing) {
      return <LockIcon />;
    }

    if (isPulsing) {
      // Show circular progress during pulse operation
      return <CircularProgress size={16} />;
    } else if (config.write_mode === 'toggle') {
      // For toggle mode, use refresh icon to indicate toggle action
      return <RefreshIcon />;
    } else if (config.write_mode === 'pulse') {
      // For pulse mode, use flash icon to indicate pulse action
      return <FlashIcon />;
    } else {
      // Fixed mode: use appropriate icon based on write value
      return config.write_value ? <PlayIcon /> : <StopIcon />;
    }
  };

  const getButtonVariant = () => {
    const state = getButtonState();
    return state === 'active' ? 'contained' : 'outlined';
  };

  const getSizeProps = () => {
    switch (config.button_size) {
      case 'small':
        return { 
          size: 'small' as const, 
          sx: { 
            minWidth: '100px', 
            height: '36px',
            fontSize: '0.875rem',
            fontWeight: 'bold'
          } 
        };
      case 'large':
        return { 
          size: 'large' as const, 
          sx: { 
            minWidth: '160px', 
            height: '64px',
            fontSize: '1.125rem',
            fontWeight: 'bold'
          } 
        };
      case 'medium':
      default:
        return { 
          size: 'medium' as const, 
          sx: { 
            minWidth: '120px', 
            height: '48px',
            fontSize: '1rem',
            fontWeight: 'bold'
          } 
        };
    }
  };

  const handleClick = () => {
    if (isButtonDisabled()) {
      return;
    }

    setError(null);

    if (config.requires_confirmation) {
      setShowConfirmation(true);
    } else {
      if (config.write_mode === 'pulse') {
        handlePulseWrite();
      } else {
        handleWrite();
      }
    }
  };

  const handleWrite = async () => {
    setIsWriting(true);
    setError(null);

    try {
      const result = await onWrite(config.id, writeReason || `Button write: ${config.name}`);
      
      if (result.success) {
        setShowConfirmation(false);
        setWriteReason('');
        // Trigger refresh to update tag value
        if (onRefresh) {
          setTimeout(onRefresh, 500); // Small delay to allow SCADA system to update
        }
      } else {
        setError(result.error || 'Write operation failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to write to SCADA tag');
    } finally {
      setIsWriting(false);
    }
  };

  const handlePulseWrite = async () => {
    setIsPulsing(true);
    setPulseProgress(0);
    setError(null);

    try {
      const originalValue = config.target_tag_current_value;
      const pulseValue = getWriteValue();
      const duration = config.pulse_duration_seconds * 1000; // Convert to milliseconds

      // Step 1: Write the pulse value
      console.log(`🔥 Pulse Step 1: Writing ${pulseValue} to ${config.target_tag_name}`);
      const pulseResult = await onWrite(config.id, writeReason || `Pulse write: ${config.name} (step 1)`);
      
      if (!pulseResult.success) {
        setError(pulseResult.error || 'Failed to start pulse operation');
        return;
      }

      // Step 1.5: Verify the write was successful by checking actual PLC value
      console.log(`✅ Pulse Step 1.5: Verifying write was successful...`);
      let verificationAttempts = 0;
      let writeVerified = false;
      const maxVerificationAttempts = 5;
      
      while (!writeVerified && verificationAttempts < maxVerificationAttempts) {
        verificationAttempts++;
        
        // Wait a bit for PLC to update
        await new Promise(resolve => setTimeout(resolve, 200));
        
        if (onRefresh) {
          onRefresh(); // Refresh to get updated tag value from PLC
        }
        
        if (onGetUpdatedValue) {
          try {
            const currentValue = await onGetUpdatedValue();
            if (currentValue === pulseValue) {
              writeVerified = true;
              console.log(`✅ Pulse write verified after ${verificationAttempts} attempts: ${currentValue}`);
            } else {
              console.log(`⏳ Pulse write verification attempt ${verificationAttempts}: expected ${pulseValue}, got ${currentValue}`);
            }
          } catch (err) {
            console.warn(`⚠️ Failed to get updated value for verification (attempt ${verificationAttempts}):`, err);
          }
        } else {
          // Fallback: assume write was successful if we don't have verification callback
          writeVerified = true;
          console.log(`⚠️ No verification callback available, assuming write was successful`);
        }
      }
      
      if (!writeVerified) {
        setError(`Failed to verify pulse write after ${maxVerificationAttempts} attempts. PLC may not have responded.`);
        return;
      }

      // Step 2: Wait for specified duration with progress indication
      console.log(`⏱️ Pulse Step 2: Waiting ${config.pulse_duration_seconds}s...`);
      const startTime = Date.now();
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / duration) * 100, 100);
        setPulseProgress(progress);
      }, 50); // Update progress every 50ms

      await new Promise(resolve => setTimeout(resolve, duration));
      clearInterval(progressInterval);

      // Step 3: Return to original value
      console.log(`🔄 Pulse Step 3: Returning to ${originalValue}`);
      // We need to write the opposite of what we just wrote
      const returnResult = await onWrite(config.id, writeReason || `Pulse write: ${config.name} (return to original)`);
      
      if (!returnResult.success) {
        setError(returnResult.error || 'Failed to complete pulse operation (return step)');
        return;
      }

      console.log(`✅ Pulse operation completed successfully`);
      setShowConfirmation(false);
      setWriteReason('');
      
      // Trigger refresh to update tag value
      if (onRefresh) {
        setTimeout(onRefresh, 500);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to execute pulse operation');
    } finally {
      setIsPulsing(false);
      setPulseProgress(0);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setWriteReason('');
    setError(null);
  };

  const getTooltipContent = () => {
    const writeValue = getWriteValue();
    const lines = [
      `Tag: ${config.target_tag_name}`,
      `Current Value: ${config.target_tag_current_value ? 'True' : 'False'}`,
    ];

    if (config.write_mode === 'toggle') {
      lines.push(`Mode: Toggle (${config.target_tag_current_value ? 'True' : 'False'} → ${writeValue ? 'True' : 'False'})`);
      if (config.text_when_false && config.text_when_true) {
        const currentText = config.target_tag_current_value ? config.text_when_true : config.text_when_false;
        const nextText = writeValue ? config.text_when_true : config.text_when_false;
        lines.push(`Will Switch: ${currentText} → ${nextText}`);
      }
    } else {
      lines.push(`Will Write: ${writeValue ? 'True' : 'False'}`);
    }

    if (config.description) {
      lines.unshift(config.description);
    }

    if (!config.can_write) {
      lines.push('⚠️ Tag not writable');
    } else if (!writeEnabled) {
      lines.push('🔒 Write session not active');
    } else if (!hasWritePermission) {
      lines.push('🔒 Insufficient permissions');
    }

    return lines.join('\n');
  };

  const sizeProps = getSizeProps();

  return (
    <>
      <Tooltip title={getTooltipContent()} placement="top" arrow>
        <Box sx={{ textAlign: 'center' }}>
          {/* Optional heading */}
          {config.button_heading && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                fontWeight: 'bold', 
                color: 'text.secondary',
                mb: 0.5,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {config.button_heading}
            </Typography>
          )}
          
          <Button
            variant={getButtonVariant()}
            color="primary"
            onClick={handleClick}
            disabled={isButtonDisabled()}
            startIcon={getButtonIcon()}
            {...sizeProps}
            sx={{
              ...sizeProps.sx,
              backgroundColor: getButtonVariant() === 'contained' ? getButtonColor() : 'transparent',
              borderColor: getButtonColor(),
              borderWidth: '2px',
              color: getButtonVariant() === 'contained' ? '#FFFFFF' : getButtonColor(),
              '&:hover': {
                backgroundColor: !isButtonDisabled() ? `${getButtonColor()}CC` : undefined,
                borderColor: getButtonColor(),
              },
              '&:disabled': {
                backgroundColor: config.disabled_color,
                borderColor: config.disabled_color,
                borderWidth: '2px',
                color: '#FFFFFF',
                opacity: 0.7,
              },
              fontWeight: 'bold',
              textTransform: 'none',
              // Ensure button is always visible
              minHeight: '36px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            {getDisplayText()}
          </Button>
        </Box>
      </Tooltip>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            Confirm Boolean Write
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {config.confirmation_message}
          </Typography>
          
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Tag:</strong> {config.target_tag_name}<br />
              <strong>Current Value:</strong> {config.target_tag_current_value ? 'True' : 'False'}<br />
              <strong>New Value:</strong> {getWriteValue() ? 'True' : 'False'}
              {config.write_mode === 'toggle' && config.text_when_false && config.text_when_true && (
                <>
                  <br />
                  <strong>Switch From:</strong> {config.target_tag_current_value ? config.text_when_true : config.text_when_false}<br />
                  <strong>Switch To:</strong> {getWriteValue() ? config.text_when_true : config.text_when_false}
                </>
              )}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={isWriting}>
            Cancel
          </Button>
          <Button
            onClick={config.write_mode === 'pulse' ? handlePulseWrite : handleWrite}
            variant="contained"
            color="primary"
            disabled={isWriting || isPulsing}
            startIcon={(isWriting || isPulsing) ? <CircularProgress size={16} /> : undefined}
          >
            {isPulsing ? 'Pulsing...' : isWriting ? 'Writing...' : config.write_mode === 'pulse' ? 'Send Pulse' : 'Confirm Write'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
